import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Gateway } from '@app/api/building/gateway/Gateway';
import { SavedEntity } from '@app/api/SavedEntity';
import { Environment, environmentToken } from '@environment';
import { Observable } from 'rxjs';
import { distinctUntilChanged, shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class GatewayService {
  public gateways: Gateway[];
  private endpoints = {
    gateway: '/gateway',
    building: '/building',
    health: '/gateway_health',
    generate: '/gateway/generatekeys'
  };
  private readonly apiUrl: string;

  constructor(private http: HttpClient, @Inject(environmentToken) environment: Environment) {
    this.apiUrl = environment.apiUrl + environment.apiPath;
  }

  getGateways(buildingId: number): Observable<Gateways> {
    return this.http.get<Gateways>(`${this.apiUrl}${this.endpoints.building}/${buildingId}/gateway`);
  }

  getGateway(gatewayId: number): Observable<Gateway> {
    return this.http.get<Gateway>(`${this.apiUrl}${this.endpoints.gateway}/${gatewayId}`);
  }

  deleteGateway(gatewayId): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}${this.endpoints.gateway}/${gatewayId}`);
  }

  addGateway(gatewayDetails: Gateway): Observable<SavedGateway> {
    return this.http.post<SavedGateway>(`${this.apiUrl}${this.endpoints.gateway}`, gatewayDetails);
  }

  updateGateway(id: number, gatewayDetails: Gateway): Observable<boolean> {
    return this.http.put<boolean>(`${this.apiUrl}${this.endpoints.gateway}/${id}`, gatewayDetails);
  }

  getGatewayFirmwareVersions(buildingId: number): Observable<GatewayFirmwareVersionDTO[]> {
    return this.http.get<GatewayFirmwareVersionDTO[]>(
      `${this.apiUrl}${this.endpoints.building}/${buildingId}/gateway-firmware-versions`
    );
  }

  getGatewayInstalledSnap(serialNumber: string): Observable<GatewayInstalledSnap> {
    return this.http.get<GatewayInstalledSnap>(
      `${this.apiUrl}${this.endpoints.gateway}/${serialNumber}/installed-snap`
    );
  }

  compareConfig(id: number): Observable<GatewayConfigComparison> {
    return this.http.get<GatewayConfigComparison>(`${this.apiUrl}${this.endpoints.gateway}/${id}/compare-config`);
  }

  configureWithRigado(id: number, gatewayDetails: Gateway): Observable<boolean> {
    return this.http.put<boolean>(`${this.apiUrl}${this.endpoints.gateway}/${id}/configure`, gatewayDetails);
  }

  produceCleanGateway(buildingId: number): Gateway {
    return new Gateway(null, null, null, buildingId);
  }

  generateKeys(gatewayId: number): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}${this.endpoints.generate}/${gatewayId}`, {});
  }

  getGatewayHealth(gatewayAddress: string): Observable<GatewayHealth> {
    return this.http
      .get<GatewayHealth>(`${this.apiUrl}${this.endpoints.health}/${gatewayAddress}?forFrontend=true`)
      .pipe(distinctUntilChanged(), shareReplay());
  }

  requestGatewayRestart(gatewayAddress: string, gatewayHealth: GatewayHealth): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}${this.endpoints.health}/${gatewayAddress}`, gatewayHealth);
  }

  produceCleanGatewayHealth(gatewayAddress: string): GatewayHealth {
    return {
      restartRequired: false,
      healthy: true,
      awsIotHealthy: true,
      meshHealthy: true,
      internetHealthy: true,
      lastRestarted: null,
      gatewayAddress
    };
  }

  downloadExport(buildingId: number): Observable<Blob> {
    return this.http.post(
      `${this.apiUrl}/building/${buildingId}/export-gateways`,
      {},
      {
        responseType: 'blob'
      }
    );
  }

  compareGatewayForMigration(buildingId: number, serialNumber: string): Observable<GatewayConfigComparison> {
    const params = new HttpParams().set('serialNumber', serialNumber);
    const url = `${this.apiUrl}${this.endpoints.gateway}/${buildingId}/configurations`;
    return this.http.get<GatewayConfigComparison>(url, { params });
  }

  migrateGatewayConfiguration(buildingId: number, gateway: Gateway): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}${this.endpoints.gateway}/${buildingId}/migrate-configuration`, gateway);
  }
}

export class Gateways {
  constructor(public gateway: Gateway[]) {}
}

export interface GatewayInstalledSnap {
  installedSnap: string;
  installedSnapRevision: string;
  supportsNmsFirmware: boolean;
}

export interface GatewayFirmwareVersionDTO {
  id: number;
  firmwareVersion: string;
  releaseDate: string;
  legacySupport: boolean;
  nmsSupport: boolean;
}

export class GatewayHealth {
  restartRequired: boolean;
  healthy: boolean;
  awsIotHealthy: boolean;
  meshHealthy: boolean;
  internetHealthy: boolean;
  lastRestarted: Date;
  gatewayAddress: string;

  constructor(
    restartRequired: boolean,
    healthy: boolean,
    awsIotHealthy: boolean,
    meshHealthy: boolean,
    internetHealthy: boolean,
    lastRestarted: Date,
    gatewayAddress: string
  ) {
    this.restartRequired = restartRequired;
    this.healthy = healthy;
    this.awsIotHealthy = awsIotHealthy;
    this.meshHealthy = meshHealthy;
    this.internetHealthy = internetHealthy;
    this.lastRestarted = lastRestarted;
    this.gatewayAddress = gatewayAddress;
  }
}

export interface GatewayConfigComparison {
  portalConfig: string;
  rigadoConfig: string;
}

type SavedGateway = SavedEntity<Gateway, number>;
